import * as React from 'react';

type ShortcutHandler = () => void;

const SEQUENCE_TIMEOUT_MS = 500;

// Normalise a shortcut string to a canonical key
// Supports: "cmd+k", "ctrl+/", "g>d" (sequential), "Escape", "?"
function parseShortcut(raw: string): { combo?: string; sequence?: string[] } {
  if (raw.includes('>')) return { sequence: raw.split('>') };
  return { combo: raw.toLowerCase() };
}

function isInputTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || el.isContentEditable;
}

function comboFromEvent(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push('cmd');
  if (e.shiftKey) parts.push('shift');
  if (e.altKey) parts.push('alt');
  const key = e.key.toLowerCase();
  // avoid doubling modifier keys
  if (!['meta', 'control', 'shift', 'alt'].includes(key)) parts.push(key);
  return parts.join('+');
}

// ── Shortcut registry (module-level singleton) ────────────────

const registry = new Map<string, Set<ShortcutHandler>>();

function register(shortcut: string, handler: ShortcutHandler): () => void {
  if (!registry.has(shortcut)) registry.set(shortcut, new Set());
  registry.get(shortcut)!.add(handler);
  return () => {
    registry.get(shortcut)?.delete(handler);
    if (registry.get(shortcut)?.size === 0) registry.delete(shortcut);
  };
}

// ── Global listener (initialised once) ───────────────────────

let listenerAttached = false;
let pendingSequence: string[] = [];
let sequenceTimer: ReturnType<typeof setTimeout> | null = null;

function clearSequence() {
  pendingSequence = [];
  if (sequenceTimer) clearTimeout(sequenceTimer);
  sequenceTimer = null;
}

function attachGlobalListener() {
  if (listenerAttached || typeof window === 'undefined') return;
  listenerAttached = true;

  window.addEventListener('keydown', (e: KeyboardEvent) => {
    if (isInputTarget(e.target)) return;

    const combo = comboFromEvent(e);

    // Check direct combos first
    if (registry.has(combo)) {
      e.preventDefault();
      registry.get(combo)!.forEach((fn) => fn());
      clearSequence();
      return;
    }

    // Sequential shortcut handling
    const nextSeq = [...pendingSequence, e.key.toLowerCase()];
    const seqKey = nextSeq.join('>');

    if (registry.has(seqKey)) {
      e.preventDefault();
      registry.get(seqKey)!.forEach((fn) => fn());
      clearSequence();
      return;
    }

    // Check if any registered sequence starts with nextSeq
    const hasPrefix = [...registry.keys()].some((k) => k.startsWith(seqKey + '>'));
    if (hasPrefix) {
      pendingSequence = nextSeq;
      if (sequenceTimer) clearTimeout(sequenceTimer);
      sequenceTimer = setTimeout(clearSequence, SEQUENCE_TIMEOUT_MS);
    } else {
      clearSequence();
    }
  });
}

// ── useShortcut hook ──────────────────────────────────────────

export function useShortcut(shortcut: string, handler: ShortcutHandler): void {
  const handlerRef = React.useRef(handler);
  handlerRef.current = handler;

  React.useEffect(() => {
    attachGlobalListener();
    const { combo, sequence } = parseShortcut(shortcut);
    const key = combo ?? sequence!.join('>');
    const stable = () => handlerRef.current();
    return register(key, stable);
  }, [shortcut]);
}

// ── useKeyboardShortcuts — registers multiple shortcuts ───────

export function useKeyboardShortcuts(
  shortcuts: Record<string, ShortcutHandler>,
): void {
  const handlersRef = React.useRef(shortcuts);
  handlersRef.current = shortcuts;

  React.useEffect(() => {
    attachGlobalListener();
    const cleanups: Array<() => void> = [];

    for (const [shortcut, _handler] of Object.entries(shortcuts)) {
      const { combo, sequence } = parseShortcut(shortcut);
      const key = combo ?? sequence!.join('>');
      const stable = () => handlersRef.current[shortcut]?.();
      cleanups.push(register(key, stable));
    }

    return () => cleanups.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
